/** Customer's own Dockie view (29 Oct). Every call is scoped server-side to the login. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { pythonApiClient } from "./pythonApiClient";
import useUserStore from "@/store/userStore";

export const CUSTOMER_VIEW_ON = process.env.REACT_APP_CUSTOMER_VIEW === "true";

const KEY = "customer-view";
const opts = { staleTime: 60 * 1000, refetchOnWindowFocus: true, retry: false };

const get = (path) => () => pythonApiClient.get(`/customer/${path}`).then((r) => r.data);

/** A stale session: the login's user is gone (the API says "User not found", e.g. after a
 *  reseed) or its token is refused. Sign out fully, not just the token, so the guard sends
 *  them to sign-in instead of a half-signed-in /home. */
const isStale = (e) =>
  e?.response?.status === 401 || (e?.response?.status === 404 && e?.response?.data?.detail === "User not found");

/** The customer this login acts for; null when it acts for none (the menu stays as it was).
 *  Every customer screen and the sidebar read this, so a stale session is caught here. */
export const useMyAccount = () =>
  useQuery({
    queryKey: [KEY, "account"],
    queryFn: () =>
      pythonApiClient
        .get("/customer/get-my-account/")
        .then((r) => r.data)
        .catch((e) => {
          if (isStale(e)) {
            useUserStore.getState().logout();
            return null;
          }
          return e?.response?.status === 404 ? null : Promise.reject(e);
        }),
    enabled: CUSTOMER_VIEW_ON,
    ...opts,
  });

export const useMyCars = () => useQuery({ queryKey: [KEY, "cars"], queryFn: get("list-my-cars/"), ...opts });
export const useMyCar = (last6) =>
  useQuery({ queryKey: [KEY, "car", last6], queryFn: get(`get-my-car/${last6}/`), enabled: !!last6, ...opts });
export const useMyDecisions = () =>
  useQuery({ queryKey: [KEY, "decisions"], queryFn: get("list-my-decisions/"), ...opts });
export const useMyInvoices = () =>
  useQuery({ queryKey: [KEY, "invoices"], queryFn: get("list-my-invoices/"), ...opts });
export const useMyDocuments = () =>
  useQuery({ queryKey: [KEY, "documents"], queryFn: get("list-my-documents/"), ...opts });

export const openMyDocument = (id) =>
  pythonApiClient.get(`/customer/open-my-document/${id}/`).then((r) => r.data.url);

export const useDecideCard = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, version, note }) =>
      pythonApiClient.post(`/customer/decide-card/${id}/`, { action, version, note }).then((r) => r.data),
    onSettled: () => client.invalidateQueries({ queryKey: [KEY] }),
  });
};

// ---- Company teams (30 Oct): members, roles, invites, and each member's own Dockie.

export const useMyTeam = () => useQuery({ queryKey: [KEY, "team"], queryFn: get("list-my-team/"), ...opts });
export const useTeamActivity = () =>
  useQuery({ queryKey: [KEY, "activity"], queryFn: get("list-team-activity/"), ...opts });
export const useDockieThreads = () =>
  useQuery({ queryKey: [KEY, "threads"], queryFn: get("list-dockie-threads/"), ...opts });
export const useDockieThread = (memberId) =>
  useQuery({
    queryKey: [KEY, "thread", memberId || "mine"],
    queryFn: get(`get-dockie-thread/${memberId ? `?member_id=${memberId}` : ""}`),
    ...opts,
  });

const useTeamMutation = (fn) => {
  const client = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => client.invalidateQueries({ queryKey: [KEY] }) });
};

export const useInviteTeammate = () =>
  useTeamMutation((body) => pythonApiClient.post("/customer/invite-teammate/", body).then((r) => r.data));
export const useChangeRole = () =>
  useTeamMutation(({ id, role }) =>
    pythonApiClient.post(`/customer/change-teammate-role/${id}/`, { role }).then((r) => r.data)
  );
export const useConfirmApprovers = () =>
  useTeamMutation((approve) => pythonApiClient.post("/customer/confirm-approvers/", { approve }).then((r) => r.data));
export const useAskDockie = () =>
  useTeamMutation((text) => pythonApiClient.post("/customer/ask-dockie/", { text }).then((r) => r.data));

// ---- Partners, containers and vendors (30 Oct).

export const useMyContainers = () =>
  useQuery({ queryKey: [KEY, "containers"], queryFn: get("list-my-containers/"), ...opts });
export const useInvitePartner = () =>
  useTeamMutation((body) => pythonApiClient.post("/customer/invite-partner/", body).then((r) => r.data));
export const useUploadPaper = () =>
  useTeamMutation(({ last6, kind, file, note }) => {
    const form = new FormData();
    form.append("kind", kind);
    form.append("file", file);
    if (note) form.append("note", note);
    return pythonApiClient
      .post(`/customer/upload-paper/${last6}/`, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  });

export const useVendorForms = () =>
  useQuery({
    queryKey: ["vendors", "forms"],
    queryFn: () => pythonApiClient.get("/vendors/list-vendor-forms/").then((r) => r.data),
    retry: false,
  });
export const applyAsVendor = (fields, files) => {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, typeof v === "string" ? v : JSON.stringify(v)));
  files.forEach(({ file }) => form.append("files", file));
  form.append("document_kinds", JSON.stringify(files.map((f) => f.kind)));
  return pythonApiClient
    .post("/vendors/apply-as-vendor/", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};
export const useMyVendor = () =>
  useQuery({
    queryKey: ["vendors", "me"],
    queryFn: () =>
      pythonApiClient
        .get("/vendors/get-my-vendor-account/")
        .then((r) => r.data)
        .catch((e) => (e?.response?.status === 404 ? null : Promise.reject(e))),
    retry: false,
  });

// ---- Joining containers, promises, forwarders' offers and the purchase questionnaire (30 Oct).

export const useNextContainers = (origin) =>
  useQuery({
    queryKey: [KEY, "next", origin || "all"],
    queryFn: get(`list-next-containers/${origin ? `?origin=${encodeURIComponent(origin)}` : ""}`),
    ...opts,
  });
export const useMyPromises = () => useQuery({ queryKey: [KEY, "promises"], queryFn: get("list-my-promises/"), ...opts });
export const usePurchaseAnswers = () =>
  useQuery({ queryKey: [KEY, "purchase"], queryFn: get("list-purchase-answers/"), ...opts });
export const useJoinContainer = () =>
  useTeamMutation(({ id, ...body }) => pythonApiClient.post(`/customer/join-container/${id}/`, body).then((r) => r.data));
export const useStartContainer = () =>
  useTeamMutation((body) => pythonApiClient.post("/customer/start-container/", body).then((r) => r.data));
export const useSavePurchase = () =>
  useTeamMutation(({ last6, ...body }) =>
    pythonApiClient.post(`/customer/save-purchase-answers/${last6}/`, body).then((r) => r.data)
  );
export const useMyOffers = () =>
  useQuery({
    queryKey: ["vendors", "offers"],
    queryFn: () => pythonApiClient.get("/vendors/list-my-container-offers/").then((r) => r.data),
    retry: false,
  });
export const useOfferContainer = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body) => pythonApiClient.post("/vendors/offer-container/", body).then((r) => r.data),
    onSettled: () => client.invalidateQueries({ queryKey: ["vendors"] }),
  });
};
